import React, { useState } from 'react';
import { Checkbox, TextInput, ActionIcon, Group, Box } from '@mantine/core';
import { X } from 'lucide-react';
import { SubTask } from '../types/todo';
import { useToggleSubTask, useUpdateSubTask, useDeleteSubTask } from '../hooks/useSubTasks';
import './SubTaskItem.css';

interface SubTaskItemProps {
  subTask: SubTask;
  isPublicView?: boolean;
}

export const SubTaskItem: React.FC<SubTaskItemProps> = ({ 
  subTask, 
  isPublicView = false 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(subTask.title);
  
  const toggleMutation = useToggleSubTask();
  const updateMutation = useUpdateSubTask();
  const deleteMutation = useDeleteSubTask();

  const handleToggle = () => {
    if (!isPublicView) {
      toggleMutation.mutate(subTask.id);
    }
  };

  const handleSaveEdit = () => {
    if (editedTitle.trim() && editedTitle !== subTask.title) {
      updateMutation.mutate({
        id: subTask.id,
        data: { title: editedTitle.trim() }
      });
    }
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (!isPublicView) {
      deleteMutation.mutate(subTask.id);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setEditedTitle(subTask.title);
      setIsEditing(false);
    }
  };

  return (
    <Group gap="xs" className="subtask-item" wrap="nowrap">
      <Checkbox
        checked={subTask.isCompleted}
        onChange={handleToggle}
        disabled={isPublicView}
        size="sm"
        styles={{
          input: {
            cursor: isPublicView ? 'default' : 'pointer',
            width: '18px',
            height: '18px',
            minWidth: '18px',
            minHeight: '18px',
            maxWidth: '18px',
            maxHeight: '18px',
            borderRadius: '4px',
            flexShrink: 0
          },
          inner: {
            width: '18px',
            height: '18px'
          }
        }}
      />
      
      {isEditing ? (
        <TextInput
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          onBlur={handleSaveEdit}
          onKeyDown={handleKeyPress}
          autoFocus
          size="sm"
          style={{ flex: 1 }}
        />
      ) : (
        <Box
          onClick={() => !isPublicView && setIsEditing(true)}
          style={{
            flex: 1,
            textDecoration: subTask.isCompleted ? 'line-through' : 'none',
            opacity: subTask.isCompleted ? 0.6 : 1,
            cursor: isPublicView ? 'default' : 'pointer',
            fontSize: '0.9rem',
            padding: '4px 0'
          }}
        >
          {subTask.title}
        </Box>
      )}
      
      {!isPublicView && (
        <ActionIcon
          size="sm"
          variant="subtle"
          color="red"
          onClick={handleDelete}
          className="subtask-delete-button"
        >
          <X size={14} />
        </ActionIcon>
      )}
    </Group>
  );
};
